import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import GlowCard from '../../../shared/GlowCard';
import { useColors } from '../../../theme/ColorModeContext';
import type { Recency, LifecycleStage } from './types';
import { RECENCY_LABELS, STAGE_HINTS, STAGE_LABELS } from './format';

export const MONO = '"Share Tech Mono", monospace';
export const DISPLAY = '"Orbitron", sans-serif';

/** A headline number with its label and an optional explanation under it. */
export const Tile = ({ label, value, hint, title }: { label: string; value: React.ReactNode; hint?: React.ReactNode; title?: string }) => {
    const colors = useColors();
    return (
        <GlowCard glowOnHover={false}>
            <Box sx={{ p: '14px 16px' }} title={title}>
                <Typography sx={{ fontFamily: MONO, fontSize: '0.64rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.text.secondary, mb: 0.5 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: '1.3rem', color: colors.text.primary, lineHeight: 1.2 }}>
                    {value}
                </Typography>
                {hint != null && (
                    <Typography sx={{ fontSize: '0.72rem', color: colors.text.muted, mt: 0.5 }}>{hint}</Typography>
                )}
            </Box>
        </GlowCard>
    );
};

export const TileGrid = ({ children, min = 170 }: { children: React.ReactNode; min?: number }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: '12px', mb: 3 }}>
        {children}
    </Box>
);

/** A titled card. `caption` says what the numbers mean; keep it honest. */
export const Section = ({ title, caption, children, action }: { title: string; caption?: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }) => {
    const colors = useColors();
    return (
        <GlowCard glowOnHover={false} sx={{ mb: 3 }}>
            <Box sx={{ p: { xs: '16px', md: '20px 22px' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                        <Typography sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: '0.9rem', textTransform: 'uppercase', color: colors.text.primary }}>
                            {title}
                        </Typography>
                        {caption && (
                            <Typography sx={{ fontSize: '0.76rem', color: colors.text.secondary, mt: 0.5, maxWidth: 760 }}>{caption}</Typography>
                        )}
                    </Box>
                    {action}
                </Box>
                {children}
            </Box>
        </GlowCard>
    );
};

export const TwoColumns = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, columnGap: 3 }}>{children}</Box>
);

export interface BarRow {
    key: string;
    label: React.ReactNode;
    value: number;
    display?: React.ReactNode;
    detail?: React.ReactNode;
    tooltip?: string;
}

/**
 * Ranked horizontal bars in one colour: the bar length carries the value, the
 * text beside it carries the exact number, so colour is never the only cue.
 */
export const BarList = ({ rows, max, empty = 'No data yet.' }: { rows: BarRow[]; max?: number; empty?: string }) => {
    const colors = useColors();
    const top = max ?? Math.max(1, ...rows.map((row) => row.value));
    if (!rows.length) {
        return <Typography sx={{ fontSize: '0.8rem', color: colors.text.muted }}>{empty}</Typography>;
    }
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {rows.map((row) => (
                <Tooltip key={row.key} title={row.tooltip || ''} placement="top-start" disableHoverListener={!row.tooltip}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: '4px' }}>
                            <Typography sx={{ fontSize: '0.8rem', color: colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row.label}
                            </Typography>
                            <Typography sx={{ fontFamily: MONO, fontSize: '0.78rem', color: colors.text.secondary, whiteSpace: 'nowrap' }}>
                                {row.display ?? row.value}
                                {row.detail != null && <Box component="span" sx={{ color: colors.text.muted, ml: 1 }}>{row.detail}</Box>}
                            </Typography>
                        </Box>
                        <Box sx={{ height: 6, borderRadius: '4px', backgroundColor: colors.border.subtle, overflow: 'hidden' }}>
                            <Box sx={{ width: `${Math.max(row.value > 0 ? 1.5 : 0, (row.value / top) * 100)}%`, height: '100%', borderRadius: '4px', backgroundColor: colors.accent.cyan }} />
                        </Box>
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
};

/** Recency is a state, so it wears status colours, always with its label. */
export const RecencyChip = ({ recency }: { recency: Recency }) => {
    const colors = useColors();
    const tone: Record<Recency, string> = {
        active: colors.accent.green,
        cooling: colors.accent.orange,
        dormant: colors.text.secondary,
        churned: colors.accent.red,
        never: colors.text.muted,
    };
    return <SmallChip label={RECENCY_LABELS[recency]} color={tone[recency]} />;
};

export const StageChip = ({ stage }: { stage: LifecycleStage }) => {
    const colors = useColors();
    const tone: Record<LifecycleStage, string> = {
        unconfirmed: colors.text.muted,
        signed_up: colors.accent.orange,
        exploring: colors.text.secondary,
        activated: colors.accent.purple,
        committed: colors.accent.green,
    };
    return <SmallChip label={STAGE_LABELS[stage]} color={tone[stage]} title={STAGE_HINTS[stage]} />;
};

export const SmallChip = ({ label, color, title }: { label: string; color: string; title?: string }) => (
    <Chip
        label={label}
        size="small"
        title={title}
        sx={{
            fontFamily: MONO,
            fontSize: '0.62rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            height: '22px',
            backgroundColor: 'transparent',
            color,
            border: `1px solid ${color}66`,
        }}
    />
);

/** Header and row styling shared by every Intel table. */
export const useTableSx = () => {
    const colors = useColors();
    return {
        head: {
            '& th': {
                fontFamily: MONO,
                fontSize: '0.66rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase' as const,
                color: colors.accent.cyan,
                borderBottom: `1px solid ${colors.border.accent}`,
                padding: '10px 12px',
                whiteSpace: 'nowrap' as const,
                backgroundColor: 'transparent',
            },
        },
        row: {
            '& td': {
                borderBottom: `1px solid ${colors.border.subtle}`,
                padding: '9px 12px',
                fontSize: '0.8rem',
                color: colors.text.primary,
                whiteSpace: 'nowrap' as const,
            },
            '&:hover': { backgroundColor: colors.accent.cyanGlow },
        },
        mono: { fontFamily: MONO, fontSize: '0.76rem', color: colors.text.secondary },
    };
};

/** Tracking only started with the activity tables; say so wherever it matters. */
export const TrackingNote = ({ since }: { since: string | null }) => {
    const colors = useColors();
    return (
        <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: colors.text.muted, mb: 2 }}>
            {since
                ? `Logins, visits and failed attempts recorded since ${since}. Solves, correct answers and quiz attempts go back to the start.`
                : 'Login, visit and attempt tracking has not recorded anything yet. Solves, correct answers and quiz attempts go back to the start.'}
        </Typography>
    );
};
